import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FacetValueIdPair, FacetWithValues } from '../../providers/facet-filter.service';
import { PriceRangeInputsComponent } from '../price-range-inputs/price-range-inputs.component';
import { DropdownContentDirective } from '../../../shared/components/dropdown/dropdown-content.directive';
import { DropdownTriggerDirective } from '../../../shared/components/dropdown/dropdown-trigger.directive';
import { DropdownComponent } from '../../../shared/components/dropdown/dropdown.component';
import { CheckboxComponent } from '../../../shared/components/checkbox/checkbox.component';
import { ComboBoxLabelDirective } from '../../../shared/components/combo-box/combo-box-label.directive';
import { ComboBoxComponent } from '../../../shared/components/combo-box/combo-box.component';
import { NgFor, NgClass, NgIf, AsyncPipe, TitleCasePipe } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faArrowRight, faChevronDown, faFilter } from "@fortawesome/free-solid-svg-icons";

@Component({
    selector: 'kbart-product-list-filters-horizontal',
    templateUrl: './product-list-filters-horizontal.component.html',
    styleUrls: ['./product-list-filters-horizontal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        FontAwesomeModule,
        NgFor,
        NgClass,
        ComboBoxComponent,
        ComboBoxLabelDirective,
        RouterLink,
        CheckboxComponent,
        DropdownComponent,
        DropdownTriggerDirective,
        DropdownContentDirective,
        PriceRangeInputsComponent,
        NgIf,
        AsyncPipe,
        TitleCasePipe,
    ],
})
export class ProductListFiltersHorizontalComponent implements OnInit, OnChanges {
    @Input() activeFacetValueIds: FacetValueIdPair[] = [];
    @Input() facets: FacetWithValues[] = [];
    @Input() featureFacetIds: null | string[] = [];
    @Input() excludeFacetIds: null | string[] = [];
    @Input() collectionSlug: string;
    min$: Observable<number | undefined>;
    max$: Observable<number | undefined>;
    activePriceRange$: Observable<{ min?: number; max?: number } | undefined>;
    displayableFacets: FacetWithValues[];
    allFacets: FacetWithValues[];
    showAllFacets = false;
    private readonly expandAllIfFewerThanFacets = 5;
    filter = faFilter;
    chevronDown = faChevronDown;
    arrowRight = faArrowRight;
    arrowLeft = faArrowLeft;

    constructor(private route: ActivatedRoute, private router: Router) {}

    ngOnInit() {
        this.min$ = this.route.queryParamMap.pipe(
            map((pm) => pm.get('min')),
            map((min) => (min != null ? +min : undefined)),
        );
        this.max$ = this.route.queryParamMap.pipe(
            map((pm) => pm.get('max')),
            map((max) => (max != null ? +max : undefined)),
        );
        this.activePriceRange$ = combineLatest(this.min$, this.max$).pipe(
            map(([min, max]) => (min != null || max != null ? { min, max } : undefined)),
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('facets' in changes || 'excludeFacetIds' in changes || 'featureFacetIds' in changes) {
            this.calculateDisplayableFacets();
        }
    }

    private calculateDisplayableFacets() {
        const nonExcludedFacets = this.facets?.filter((f) => !this.excludeFacetIds?.includes(f.id)) ?? [];
        this.allFacets = nonExcludedFacets.sort((a, b) => {
            return this.featureFacetIds?.includes(a.id) ? -1 : 1;
        });
        this.displayableFacets = this.allFacets
            .filter((f) =>
                this.showAllFacets
                    ? true
                    : this.featureFacetIds?.length
                    ? this.featureFacetIds.includes(f.id)
                    : true,
            )
            .map((facet) => {
                facet.values = facet.values.filter((fv) => 0 < fv.count);
                return facet;
            });
    }

    showAll() {
        this.showAllFacets = true;
        this.calculateDisplayableFacets();
    }

    showFeatured() {
        this.showAllFacets = false;
        this.calculateDisplayableFacets();
    }

    toggleFacetValueId(facetId: string, valueId: string): string[] {
        const pair = `${facetId}-${valueId}` as const;
        const existing = this.activeFacetValueIds;
        return existing.includes(pair) ? existing.filter((x) => x !== pair) : existing.concat(pair);
    }

    trackById(index: number, item: { id: string }) {
        return item.id;
    }

    setPriceRange(range: { min?: number; max?: number }) {
        const params: any = {};
        if (range.min != null) {
            params.min = range.min;
        }
        if (range.max != null) {
            params.max = range.max;
        }
        this.router.navigate(['./'], {
            queryParams: params,
            queryParamsHandling: 'merge',
            relativeTo: this.route,
        });
    }
}
